import { api } from 'src/api/axios';

export interface Applicant {
  applicant: number;
  firstName: string;
  lastName: string;
  midName: string;
  phone: string;
  gender: string;
  dob: string;
  nicotine: boolean;
  weight: number;
  heightFt: number;
  heightIn: number;
}

export interface Address {
  address1: string;
  address2: string;
  city: string;
  state: string;
  zipcode: string;
}

export interface Plan {
  id: number;
  priceId: number;
  applicants: string[];
}

export interface Medication {
  applicant: string;
  name: string;
  rxcui: string;
  dosage: string;
  frequency: string;
  frequencyDescription: string;
  description: string;
}

export interface IssueDetail {
  key: string;
  description: string;
}

export interface Issue {
  key: string;
  details: IssueDetail[];
}

export interface Condition {
  key: string;
  description: string;
}

export interface Phq {
  treatment: boolean;
  invalid: boolean;
  pregnancy: boolean;
  disclaimer: boolean;
  signature: string;
  effectiveDate: string;
  medications: Medication[];
  issues: Issue[];
  conditions: Condition[];
}

export interface ApplicationPayload {
  applicants: Applicant[];
  income: string;
  address: Address;
  plans: Plan[];
  phq: Phq;
}

export interface Signature {
  id: string;
  createdAt: string;
  updatedAt: string;
  signatureRequestId: string;
  signedAt: string;
}

export interface Application {
  id: string;
  createdAt: string;
  updatedAt: string;
  payload: ApplicationPayload;
  status: number;
  submittedAt: string;
  underReviewAt: string;
  signature: Signature;
}

export interface RejectRequest {
  note?: string;
}

export interface AcceptRequest {
  tier: string;
  note?: string;
}

export interface RejectResponse {
  id: string;
  createdAt: string;
  updatedAt: string;
  status: number;
  priceId: number;
  reviewedBy: number;
  reason: string;
  conclusion: string;
  note: string;
}

export const applicationReviewsService = {
  list: async (params?: {
    status?: number;
    search?: string;
    offset?: number;
    limit?: number;
    column?: string;
    order?: string;
  }) => {
    // Удаляем короткие поисковые запросы
    if (params?.search && params.search.length < 2) {
      delete params.search;
    }
    
    // Приводим числовые параметры к строкам для query параметров
    // поскольку query параметры всегда строка
    const queryParams: Record<string, string> = {};
    
    if (params?.status !== undefined) {
      queryParams.status = String(params.status);
    }
    if (params?.search) {
      queryParams.search = params.search;
    }
    if (params?.offset !== undefined) {
      queryParams.offset = String(params.offset);
    }
    if (params?.limit !== undefined) {
      queryParams.limit = String(params.limit);
    }
    if (params?.column) {
      queryParams.column = params.column;
    }
    if (params?.order) {
      queryParams.order = params.order;
    }
    
    const response = await api.get('/application-reviews', { params: queryParams });
    return response.data;
  },

  getOne: async (id: string | number) => {
    const response = await api.get(`/application-reviews/${id}`);
    return response.data;
  },

  reject: async (id: string | number, data: RejectRequest): Promise<RejectResponse> => {
    const response = await api.post(`/application-reviews/${id}/reject`, data);
    return response.data;
  },

  accept: async (id: string | number, data: AcceptRequest): Promise<RejectResponse> => {
    const response = await api.post(`/application-reviews/${id}/accept`, data);
    return response.data;
  },
};
